import posthogService, { CaptureProperties } from '@/services/posthog-service';

/**
 * Product analytics for the whole app: one `track` call sends the event to PostHog (live app only, see
 * posthog-service) and mirrors it to Microsoft Clarity as a custom event, so recordings can be filtered
 * by what happened in them. Feature areas name their own events on top of this (e.g. onboarding-analytics).
 *
 * Dev mode logs every event to the browser console, so events can be checked locally and on UAT, where
 * PostHog never sends, and keeps the latest ones in `scAnalytics.events`. It is on by default in local
 * dev builds and can be switched in any browser:
 *   scAnalytics.devMode(true)   // log events in this browser
 *   scAnalytics.devMode(false)  // stop logging
 *   scAnalytics.devMode(null)   // back to the default for this build
 *   scAnalytics.events          // the events logged since the page loaded (newest last)
 */

/** Change to force dev mode on or off for every build; null follows the build (on for `vite dev`). */
export const ANALYTICS_DEV_MODE: boolean | null = null;

export const ANALYTICS_DEV_MODE_KEY = 'sc:analytics:devMode';

const readStoredDevMode = (): boolean | null => {
    try {
        const raw = window.localStorage.getItem(ANALYTICS_DEV_MODE_KEY);
        return raw === 'on' ? true : raw === 'off' ? false : null;
    } catch {
        return null;
    }
};

export const isAnalyticsDevMode = (): boolean => {
    if (typeof window === 'undefined') return false;
    return readStoredDevMode() ?? ANALYTICS_DEV_MODE ?? !!import.meta.env.DEV;
};

export const setAnalyticsDevMode = (on: boolean | null) => {
    try {
        if (on === null) window.localStorage.removeItem(ANALYTICS_DEV_MODE_KEY);
        else window.localStorage.setItem(ANALYTICS_DEV_MODE_KEY, on ? 'on' : 'off');
    } catch {
        // storage disabled: nothing to remember
    }
};

export interface LoggedAnalyticsEvent {
    name: string;
    properties: CaptureProperties;
    setOnce?: CaptureProperties;
    at: string;
}

const MAX_LOGGED_EVENTS = 200;
const loggedEvents: LoggedAnalyticsEvent[] = [];

if (typeof window !== 'undefined') {
    (window as any).scAnalytics = {
        devMode: (on: boolean | null = true) => {
            setAnalyticsDevMode(on);
            return isAnalyticsDevMode();
        },
        events: loggedEvents,
    };
}

type Clarity = (...args: unknown[]) => void;
const clarity = (): Clarity | null => (typeof window !== 'undefined' && typeof (window as any).clarity === 'function' ? (window as any).clarity : null);

const devLog = (kind: string, name: string, detail?: unknown) => {
    // eslint-disable-next-line no-console
    if (isAnalyticsDevMode()) console.info(`[analytics] ${kind}: ${name}`, detail ?? '');
};

export interface TrackOptions {
    /** Person properties PostHog keeps from the first time they're sent, e.g. when onboarding ended. */
    setOnce?: CaptureProperties;
}

/** Records an event. Never throws: analytics must not break the app. */
const track = (event: string, properties: CaptureProperties = {}, options: TrackOptions = {}) => {
    try {
        if (isAnalyticsDevMode()) {
            loggedEvents.push({ name: event, properties, ...(options.setOnce ? { setOnce: options.setOnce } : {}), at: new Date().toISOString() });
            if (loggedEvents.length > MAX_LOGGED_EVENTS) loggedEvents.shift();
        }
        devLog('event', event, { properties, ...(options.setOnce ? { setOnce: options.setOnce } : {}) });
        clarity()?.('event', event);
        void posthogService.capture(event, properties, options.setOnce);
    } catch {
        // ignore
    }
};

/** Tags the current Clarity session (e.g. "this session saw onboarding"), for filtering recordings. */
const tagSession = (key: string, value: string) => {
    try {
        devLog('session tag', key, value);
        clarity()?.('set', key, value);
    } catch {
        // ignore
    }
};

const analyticsService = {
    track,
    tagSession,
};

export default analyticsService;
